import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "query",
    "results",
    "resultsPanel",
    "status",
    "selectionFields",
    "nameField",
    "nctField",
    "govJsonField",
    "sponsorField",
    "changeStudyButton"
  ];

  static values = {
    searchUrl: String,
    debounceMs: { type: Number, default: 400 }
  };

  connect() {
    this.debounceTimer = null;
    this.abortController = null;
    this.selectedIndex = -1;
    this.resultsData = [];
    if (this.hasNctFieldTarget && this.nctFieldTarget.value) {
      this.restoreSelectedFromFields();
    }
  }

  restoreSelectedFromFields() {
    this.showSelectedState();
  }

  disconnect() {
    this.clearDebounce();
    this.abortInflight();
  }

  onInput() {
    if (this.hasSelectionFieldsTarget && !this.selectionFieldsTarget.classList.contains("hidden")) {
      return;
    }
    this.clearDebounce();
    this.debounceTimer = window.setTimeout(() => this.search(), this.debounceMsValue);
  }

  onKeydown(event) {
    if (!this.hasResultsPanelTarget || this.resultsPanelTarget.classList.contains("hidden")) {
      return;
    }

    if (event.key === "Escape") {
      this.hideResults();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.moveSelection(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      this.moveSelection(-1);
      return;
    }

    if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault();
      this.selectResult(this.resultsData[this.selectedIndex]);
    }
  }

  async search() {
    const q = this.queryTarget.value.trim();
    if (q.length < 2) {
      this.hideResults();
      return;
    }

    this.abortInflight();
    this.abortController = new AbortController();
    this.setStatus("Searching ClinicalTrials.gov…");

    try {
      const url = new URL(this.searchUrlValue, window.location.origin);
      url.searchParams.set("q", q);
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      });

      const payload = await response.json();
      if (!response.ok) {
        this.setStatus(payload.error || "ClinicalTrials.gov is unavailable. Try again later.");
        this.renderResults([]);
        return;
      }

      this.renderResults(payload.results || []);
      this.setStatus("");
    } catch (error) {
      if (error.name === "AbortError") return;
      this.setStatus("ClinicalTrials.gov is unavailable. Try again later.");
      this.renderResults([]);
    }
  }

  renderResults(results) {
    this.resultsData = results;
    this.selectedIndex = results.length ? 0 : -1;

    if (!results.length) {
      this.resultsTarget.innerHTML = "<li class=\"p-3 text-sm text-gray-600\">No matching ClinicalTrials.gov studies found.</li>";
      this.showResults();
      return;
    }

    this.resultsTarget.innerHTML = results
      .map((hit, index) => {
        const phases = Array.isArray(hit.phase) ? hit.phase.join(", ") : "";
        return `
          <li>
            <button type="button"
                    class="w-full text-left p-3 hover:bg-gray-100 border-b border-gray-200 prescreen-ctgov-result ${index === 0 ? "bg-gray-50" : ""}"
                    data-action="click->prescreen-clinical-trials-search#pickResult"
                    data-index="${index}">
              <div class="font-medium text-gray-900">${this.escapeHtml(hit.title)}</div>
              <div class="text-xs text-gray-600 mt-1">
                ${this.escapeHtml(hit.nctId)} • ${this.escapeHtml(hit.sponsor || "—")} • ${this.escapeHtml(hit.status || "—")}
              </div>
              ${phases ? `<div class="text-xs text-gray-500 mt-1">${this.escapeHtml(phases)}</div>` : ""}
            </button>
          </li>`;
      })
      .join("");

    this.showResults();
  }

  pickResult(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    this.selectResult(this.resultsData[index]);
  }

  selectResult(hit) {
    if (!hit) return;

    this.nameFieldTarget.value = hit.title || "";
    if (this.hasSponsorFieldTarget) {
      this.sponsorFieldTarget.value = hit.sponsor || "";
    }
    this.nctFieldTarget.value = hit.nctId || "";
    this.govJsonFieldTarget.value = JSON.stringify(hit);

    this.showSelectedState();
    this.hideResults();
    this.setStatus("");
  }

  changeStudy(event) {
    event.preventDefault();
    this.nctFieldTarget.value = "";
    this.govJsonFieldTarget.value = "";
    this.queryTarget.value = "";
    this.hideSelectedState();
    this.queryTarget.focus();
  }

  showSelectedState() {
    this.selectionFieldsTarget.classList.remove("hidden");
    this.changeStudyButtonTarget.classList.remove("hidden");
    this.queryTarget.readOnly = true;
  }

  hideSelectedState() {
    this.selectionFieldsTarget.classList.add("hidden");
    this.changeStudyButtonTarget.classList.add("hidden");
    this.queryTarget.readOnly = false;
  }

  moveSelection(delta) {
    if (!this.resultsData.length) return;
    this.selectedIndex = (this.selectedIndex + delta + this.resultsData.length) % this.resultsData.length;
    this.resultsTarget.querySelectorAll(".prescreen-ctgov-result").forEach((el, idx) => {
      el.classList.toggle("bg-gray-50", idx === this.selectedIndex);
    });
  }

  showResults() {
    this.resultsPanelTarget.classList.remove("hidden");
  }

  hideResults() {
    this.resultsPanelTarget.classList.add("hidden");
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = message;
    this.statusTarget.classList.toggle("hidden", !message);
  }

  clearDebounce() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  abortInflight() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
